import { Box, HStack, VStack } from "@chakra-ui/react"
import { forwardRef, useMemo } from "react"
import {
  HomeResourceFragment,
  ResourceBackgroundType
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../../pathwright/PathwrightContext"
import { useHomeContext } from "../../HomeContext"
import { getCertificateDownloadUrl, getRegistrationPathLink } from "../../utils"
import { GroupAvatars } from "../GroupAvatars"
import PendingReviewCount from "../PendingReviewCount"
import NestedPaths from "./NestedPaths"
import PathCard, {
  PathCardBackground,
  PathCardCertificate,
  PathCardContent,
  PathCardImage,
  PathCardProgress,
  PathCardTitle
} from "./PathCard"
import PathCardAction from "./PathCardAction"
import PathCardRegistrationControls from "./PathCardRegistrationControls"
import { RegistrationPathCardProps } from "./types"

type PathCardBackgroundProps = {
  resource: Pick<
    HomeResourceFragment,
    | "background_image"
    | "cover_image"
    | "background_type"
    | "background_overlay"
  >
}

export const RegistrationPathCardBackground = ({
  resource
}: PathCardBackgroundProps) => {
  // When the background image is set to blur, we use the cover image as the background
  // and then overlay the cover image with the blurred background image (at a lower opacity).
  let bgImage = ""
  let bgImageOverlay = ""

  if (
    resource.background_image &&
    resource.background_type === ResourceBackgroundType.Blur
  ) {
    bgImage = resource.cover_image
    bgImageOverlay = resource.background_image
  } else {
    bgImage = resource.background_image || resource.cover_image
  }

  return (
    <PathCardBackground
      backgroundImage={bgImage}
      backgroundImageOverlay={bgImageOverlay}
      backgroundOverlay={resource.background_overlay}
    />
  )
}

const RegistrationPathCard = forwardRef<
  HTMLDivElement,
  RegistrationPathCardProps
>(
  (
    { registration, parentRegistration, nestedRegistrations, enableLink },
    ref
  ) => {
    const resource = registration.resource!
    const pwContext = usePathwrightContext()
    const { getPendingReviewCount } = useHomeContext()
    const pendingReviewCount = getPendingReviewCount(registration)

    // Default to enabling link.
    enableLink ??= true

    const certificateDownloadUrl = useMemo(
      () => getCertificateDownloadUrl({ pwContext, registration }),
      [pwContext, registration]
    )

    const pathLink = useMemo(
      () => getRegistrationPathLink(registration),
      [registration]
    )

    return (
      <PathCard
        ref={ref}
        id={`PathCard__${registration.group.id}`}
        // Using "group" className to allow revealing of PathCardRegistrationControls
        // on hover of the PathCard.
        className="PathCard group"
      >
        <RegistrationPathCardBackground resource={resource!} />
        <PathCardProgress progress={registration.path?.progress} />
        <PathCardContent>
          <PathCardImage image={resource.image!} title={resource.name} />
          <VStack
            gap={2}
            align="flex-start"
            justify="center"
            flexGrow={1}
            minW="0"
          >
            <PathCardTitle link={enableLink ? pathLink : undefined}>
              {resource.name}
            </PathCardTitle>
            {
              // Moderators and Teachers should see list of Cohort members.
              [10, 15].includes(registration.role) && (
                <GroupAvatars group={registration.group} showGroupName />
              )
            }
            <HStack w="100%" gap={4} _empty={{ display: "none" }}>
              <PathCardAction registration={registration} />
              <PathCardCertificate link={certificateDownloadUrl} />
            </HStack>
            <NestedPaths nestedRegistrations={nestedRegistrations} />
          </VStack>
        </PathCardContent>
        <Box pos="absolute" right={2.5} top={2}>
          <HStack>
            <PendingReviewCount item={registration} hideOnHover={false} />
            <PathCardRegistrationControls
              registration={registration}
              parentRegistration={parentRegistration}
              // NOTE: rendering portal to overlay toolbar. Hopefully the UX tradeoff
              // is nominal, but this does mean the menu is slightly jerkey when scrolling.
              renderPortal
              renderOnGroupHover={!pendingReviewCount}
              triggerProps={{
                size: "2xs"
              }}
            />
          </HStack>
        </Box>
      </PathCard>
    )
  }
)

export default RegistrationPathCard
